import { Icon } from '@iconify/react';
import lockIcon from '@iconify/icons-material-symbols/lock';
import lockOpenRightIcon from '@iconify/icons-material-symbols/lock-open-right';
import { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import SingleDatePicker from '../components/SingleDatePicker/SingleDatePicker';
import { selectClassroom } from '../Classrooms';
import { getMiddleSchoolUnit, submitAttendance } from '../BackendInterface';
import { getLocalForage, removeLocalForage, setLocalForage } from '../aaa';
import { RootState } from '../ApplicationState';
import { StudentProxyType } from '@peekapak/types/DataProtocolTypes';
import { Lesson, UnitResult } from '@peekapak/types/LessonPlanTypes';
import {
  AttendanceDraft,
  AttendanceStatus,
  buildAttendanceCacheKey,
  buildLessonObjects,
  buildSubmissionPayload,
  countAttendance,
  formatAttendanceDateForControl,
  getTodayAttendanceDate,
  initializeAttendanceStatuses,
} from './attendanceHelpers';

interface UrlParams {
  unitId: string;
  language: string;
  grade: string;
}

interface AttendanceDraftFromCache {
  attendanceStatuses: AttendanceDraft['attendanceStatuses'];
  attendanceDate?: AttendanceDraft['attendanceDate'];
  isAttendanceDateLocked?: AttendanceDraft['isAttendanceDateLocked'];
  selectedLessons?: AttendanceDraft['selectedLessons'];
  selectedSessions?: AttendanceDraft['selectedLessons'];
}

const PRIMARY_BUTTON_CLASS =
  "rounded-[0.5rem] border-0 bg-[#00afd8] px-6 py-[1.6rem] font-['GT-Walsheim-Pro-Bold'] text-[1.6rem] uppercase text-white transition-[background_250ms_ease-in-out,transform_150ms_ease] hover:bg-[#009fc4] focus:outline-none focus:ring-2 focus:ring-[#009fc4] disabled:cursor-not-allowed disabled:opacity-40 active:bg-[#0290b2]";

const PRIMARY_BUTTON_SMALL_CLASS =
  "rounded-[0.5rem] border-0 bg-[#00afd8] px-5 py-2 font-['GT-Walsheim-Pro-Bold'] text-[1.4rem] uppercase text-white transition-[background_250ms_ease-in-out,transform_150ms_ease] hover:bg-[#009fc4] focus:outline-none focus:ring-2 focus:ring-[#009fc4] disabled:cursor-not-allowed disabled:opacity-40 active:bg-[#0290b2]";

const SECONDARY_BUTTON_SMALL_CLASS =
  "rounded-[0.5rem] border-2 border-[#00afd8] bg-white px-5 py-2 font-['GT-Walsheim-Pro-Bold'] text-[1.4rem] uppercase text-[#00afd8] transition-[background_250ms_ease-in-out] hover:bg-[#e6f9fd] focus:outline-none focus:ring-2 focus:ring-[#009fc4] disabled:cursor-not-allowed disabled:opacity-40";

const DUPLICATE_ATTENDANCE_ERROR_MESSAGE = 'Duplicate attendance submission';
const FRIENDLY_DUPLICATE_ATTENDANCE_MESSAGE =
  'Attendance has already been submitted for this date and lesson selection.';
const CALENDAR_OUTLINE_ICON = 'famicons:calendar-outline';

// ---------------------------------------------------------------------------
// Student card sub-component
// ---------------------------------------------------------------------------

interface StudentCardProps {
  student: StudentProxyType;
  status: AttendanceStatus;
  onToggle: (studentUserId: string) => void;
}

function StudentCard({ student, status, onToggle }: StudentCardProps) {
  const isPresent = status === 'present';
  return (
    <button
      type='button'
      onClick={() => onToggle(student.userId)}
      className={[
        'rounded-lg border-2 p-4 text-left cursor-pointer transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 w-full',
        isPresent
          ? 'border-[#7fd7eb] bg-[#e7f4f7] hover:bg-[#cdecf4] focus:ring-[#009fc4]'
          : 'bg-yellow-100 border-yellow-300 hover:bg-yellow-200 focus:ring-yellow-400',
      ].join(' ')}
      aria-pressed={isPresent}
      aria-label={`${student.firstName}${student.lastName ? ` ${student.lastName}` : ''} — ${isPresent ? 'Present' : 'Absent'}. Click to toggle.`}
    >
      <span className='block font-semibold text-gray-800 text-[1.75rem] leading-snug'>
        {student.firstName}
        {student.lastName ? ` ${student.lastName}` : ''}
      </span>
      <span
        className={['block text-[1.5rem] mt-1 font-medium', isPresent ? 'text-[#00afd8]' : 'text-yellow-600'].join(' ')}
      >
        {isPresent ? 'Present' : 'Absent'}
      </span>
    </button>
  );
}

function LockIcon({ locked }: { locked: boolean }) {
  return <Icon icon={locked ? lockIcon : lockOpenRightIcon} className='h-11 w-11' aria-hidden='true' />;
}

function CalendarIcon() {
  return <Icon icon={CALENDAR_OUTLINE_ICON} className='h-11 w-11' aria-hidden='true' />;
}

function CheckCircleIcon() {
  return (
    <svg viewBox='0 0 64 64' aria-hidden='true' className='h-16 w-16 text-[#42c86f]'>
      <circle cx='32' cy='32' r='24' fill='none' stroke='currentColor' strokeWidth='2.5' />
      <path
        d='M22 32.5 29 39.5 43 24.5'
        fill='none'
        stroke='currentColor'
        strokeWidth='3.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const TakweenAttendancePage = (): JSX.Element => {
  const { unitId, language, grade } = useParams<UrlParams>();
  const history = useHistory();
  // MainLayout guarantees classrooms are loaded before rendering child routes
  const classroom = useSelector((state: RootState) => selectClassroom(state));

  // Form state
  const [selectedLessons, setSelectedLessons] = useState<Set<string>>(new Set());
  const [attendanceStatuses, setAttendanceStatuses] = useState<Record<string, AttendanceStatus>>({});
  const [attendanceDate, setAttendanceDate] = useState<string>(getTodayAttendanceDate());
  const [isAttendanceDateLocked, setIsAttendanceDateLocked] = useState(true);

  // Unit data (for display name + lesson list)
  const [unitDisplayName, setUnitDisplayName] = useState<string>('');
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [isLoadingUnit, setIsLoadingUnit] = useState(true);

  // Overlay state
  const [showConfirmOverlay, setShowConfirmOverlay] = useState(false);
  const [showResultOverlay, setShowResultOverlay] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const locale = typeof window === 'undefined' ? 'en-US' : window.navigator.language || 'en-US';

  // ---------------------------------------------------------------------------
  // On mount: load unit data
  // ---------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    async function loadUnit() {
      setIsLoadingUnit(true);
      try {
        // BackendInterface types getMiddleSchoolUnit as Promise<Unit> but actually returns UnitResult at runtime
        const unitPayload = (await getMiddleSchoolUnit(unitId, language, grade)) as unknown as UnitResult;
        if (cancelled) return;
        const unit = unitPayload.data;
        setUnitDisplayName(unit.displayName ?? '');
        const sortedLessons = [...(unit.lessons?.content ?? [])].sort((a, b) => a.index - b.index);
        setLessons(sortedLessons);
      } catch (err) {
        console.error('TakweenAttendancePage: failed to load unit', err);
      }
      if (!cancelled) setIsLoadingUnit(false);
    }
    loadUnit();
    return () => {
      cancelled = true;
    };
  }, [unitId, language, grade]);

  // Keep latest form state available without retriggering the classroom-switch effect.
  const formStateRef = useRef({
    selectedLessons,
    attendanceStatuses,
    attendanceDate,
    isAttendanceDateLocked,
  });
  useEffect(() => {
    formStateRef.current = {
      selectedLessons,
      attendanceStatuses,
      attendanceDate,
      isAttendanceDateLocked,
    };
  }, [selectedLessons, attendanceStatuses, attendanceDate, isAttendanceDateLocked]);

  const lessonsRef = useRef(lessons);
  useEffect(() => {
    lessonsRef.current = lessons;
  }, [lessons]);

  // ---------------------------------------------------------------------------
  // On classroom change:
  // 1) silently preserve current classroom draft,
  // 2) restore incoming classroom draft (or initialize defaults).
  // ---------------------------------------------------------------------------
  const previousClassroomIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!classroom?.listOfStudentProxies || !classroom.classroomId) return;

    const incomingClassroomId = classroom.classroomId;
    const outgoingClassroomId = previousClassroomIdRef.current;
    if (incomingClassroomId === outgoingClassroomId) return;

    let cancelled = false;

    async function initStudents() {
      if (outgoingClassroomId) {
        const current = formStateRef.current;
        const outgoingDraft: AttendanceDraft = {
          selectedLessons: buildLessonObjects(current.selectedLessons, lessonsRef.current),
          attendanceStatuses: current.attendanceStatuses,
          attendanceDate: current.attendanceDate,
          isAttendanceDateLocked: current.isAttendanceDateLocked,
        };
        await setLocalForage(buildAttendanceCacheKey(outgoingClassroomId), outgoingDraft);
      }

      previousClassroomIdRef.current = incomingClassroomId;

      const sorted = [...classroom!.listOfStudentProxies].sort(
        (a, b) => a.firstName.localeCompare(b.firstName) || (a.lastName ?? '').localeCompare(b.lastName ?? ''),
      );
      const initialStatuses = initializeAttendanceStatuses(sorted);

      const cacheKey = buildAttendanceCacheKey(incomingClassroomId);
      const draft = (await getLocalForage(cacheKey)) as AttendanceDraftFromCache | null;
      if (cancelled) return;

      if (draft) {
        // Support legacy 'selectedSessions' key for old cached drafts
        const lessonArray = draft.selectedLessons ?? draft.selectedSessions ?? [];
        setSelectedLessons(new Set(lessonArray.map(({ lessonId }) => lessonId)));
        setAttendanceStatuses(draft.attendanceStatuses);
        setAttendanceDate(draft.attendanceDate ?? getTodayAttendanceDate());
        setIsAttendanceDateLocked(draft.isAttendanceDateLocked ?? true);
        await removeLocalForage(cacheKey);
      } else {
        setSelectedLessons(new Set());
        setAttendanceStatuses(initialStatuses);
        setAttendanceDate(getTodayAttendanceDate());
        setIsAttendanceDateLocked(true);
      }
    }
    initStudents();

    return () => {
      cancelled = true;
    };
  }, [classroom]);

  // ---------------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------------

  const sortedStudents: StudentProxyType[] = [...(classroom?.listOfStudentProxies ?? [])].sort(
    (a, b) => a.firstName.localeCompare(b.firstName) || (a.lastName ?? '').localeCompare(b.lastName ?? ''),
  );

  const attendanceCounts = countAttendance(attendanceStatuses);
  const selectedLessonObjects = buildLessonObjects(selectedLessons, lessons);
  const hasSelectedLessons = selectedLessonObjects.length > 0;
  const isSubmitDisabled = isSubmitting || !hasSelectedLessons;

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  function handleToggleLesson(lessonId: string) {
    setSelectedLessons((prev) => {
      const next = new Set(prev);
      if (next.has(lessonId)) {
        next.delete(lessonId);
      } else {
        next.add(lessonId);
      }
      return next;
    });
  }

  function handleToggleStudent(studentUserId: string) {
    setAttendanceStatuses((prev) => ({
      ...prev,
      [studentUserId]: (prev[studentUserId] ?? 'present') === 'present' ? 'absent' : 'present',
    }));
  }

  function handleCancel() {
    history.push(`/lessonPlan/${unitId}/${language}/${grade}`);
  }

  async function handleSave() {
    if (classroom?.classroomId) {
      const draft: AttendanceDraft = {
        selectedLessons: buildLessonObjects(selectedLessons, lessons),
        attendanceStatuses,
        attendanceDate,
        isAttendanceDateLocked,
      };
      await setLocalForage(buildAttendanceCacheKey(classroom.classroomId), draft);
    }
    history.push(`/lessonPlan/${unitId}/${language}/${grade}`);
  }

  function handleSubmitClick() {
    if (isSubmitDisabled) return;
    setShowConfirmOverlay(true);
  }

  function handleUnlockAttendanceDate() {
    setIsAttendanceDateLocked((previous) => !previous);
  }

  function handleConfirmCancel() {
    setShowConfirmOverlay(false);
  }

  async function handleConfirmSubmit() {
    if (!classroom?.classroomId) return;
    setIsSubmitting(true);
    setShowConfirmOverlay(false);
    try {
      if (!unitDisplayName) {
        setSubmissionError('Course information has not loaded. Please refresh and try again.');
        setIsSubmitting(false);
        return;
      }
      const payload = buildSubmissionPayload(
        classroom.classroomId,
        unitId,
        attendanceDate,
        selectedLessons,
        attendanceStatuses,
        lessons,
        unitDisplayName,
      );
      if (payload.lessons.length === 0) {
        throw new Error('Please select at least one lesson.');
      }
      await submitAttendance(payload);
      setSubmissionError(null);
      setShowResultOverlay(true);
      setTimeout(() => {
        history.push(`/lessonPlan/${unitId}/${language}/${grade}`);
      }, 1500);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unknown error occurred.';
      setSubmissionError(
        message.includes(DUPLICATE_ATTENDANCE_ERROR_MESSAGE)
          ? FRIENDLY_DUPLICATE_ATTENDANCE_MESSAGE
          : 'The attendance could not be submitted. Please try again.',
      );
      setShowResultOverlay(true);
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleResultOkay() {
    setShowResultOverlay(false);
    setSubmissionError(null);
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className='min-h-screen bg-gray-50 text-gray-900'>
      {/* Loading state */}
      {isLoadingUnit && (
        <div className='flex items-center justify-center py-20'>
          <p className='text-gray-500 text-[2.25rem]'>Loading…</p>
        </div>
      )}

      {!isLoadingUnit && (
        <>
          {/* Page header */}
          <header className='bg-white border-b border-gray-200 px-6 py-5'>
            <h1 className='text-[3rem] font-bold text-gray-900 mb-4'>{unitDisplayName}</h1>

            <div className='mb-6 max-w-[46rem]'>
              <div className='flex items-center justify-between gap-4 rounded-2xl border border-[#7fd7eb] bg-white px-5 py-3 shadow-[0_4px_12px_rgba(0,175,216,0.12)]'>
                <div className='flex min-w-0 flex-1 items-center gap-4'>
                  <div className='flex shrink-0 items-center justify-center text-[#00afd8]'>
                    <CalendarIcon />
                  </div>
                  <div className='flex min-w-0 flex-1 items-center gap-3'>
                    <label
                      htmlFor='attendance-date-input'
                      className='shrink-0 text-[1.9rem] font-semibold text-slate-800'
                    >
                      Date:
                    </label>
                    <div className='min-w-0 flex-1'>
                      <SingleDatePicker
                        inputId='attendance-date-input'
                        value={attendanceDate}
                        onChange={setAttendanceDate}
                        disabled={isAttendanceDateLocked}
                        locale={locale}
                      />
                    </div>
                  </div>
                </div>
                <button
                  type='button'
                  onClick={handleUnlockAttendanceDate}
                  aria-label={isAttendanceDateLocked ? 'Unlock attendance date' : 'Lock attendance date'}
                  className={[
                    'flex h-12 w-12 shrink-0 appearance-none items-center justify-center rounded-lg border-0 bg-transparent p-0 shadow-none transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-[#009fc4]',
                    isAttendanceDateLocked ? 'text-slate-500 hover:opacity-80' : 'text-[#00afd8] hover:opacity-80',
                  ].join(' ')}
                >
                  <LockIcon locked={isAttendanceDateLocked} />
                </button>
              </div>
              <p className='mt-3 text-center text-[1.45rem] text-slate-600'>
                {isAttendanceDateLocked
                  ? "Locked to today's date. Click the lock to change."
                  : 'Choose the date you want to apply this attendance to.'}
              </p>
            </div>

            <div className='flex flex-wrap gap-6 items-start'>
              {/* Lessons */}
              <fieldset className='flex flex-col gap-1'>
                <legend className='text-[1.75rem] font-semibold text-gray-700 mb-1'>Lessons</legend>
                <div className='grid grid-cols-1 gap-x-6 gap-y-2 md:grid-cols-2'>
                  {lessons.map((lesson, idx) => {
                    const checkboxId = `lesson-checkbox-${lesson.lessonId}`;
                    return (
                      <label
                        key={lesson.lessonId}
                        htmlFor={checkboxId}
                        className='flex cursor-pointer items-center gap-2 text-[1.75rem]'
                      >
                        <input
                          id={checkboxId}
                          type='checkbox'
                          checked={selectedLessons.has(lesson.lessonId)}
                          onChange={() => handleToggleLesson(lesson.lessonId)}
                          className='h-4 w-4 shrink-0 rounded border-gray-400 text-[#00afd8] focus:ring-[#009fc4]'
                        />
                        <span>
                          Lesson {idx + 1}: {lesson.title}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            </div>
          </header>

          {/* Student card grid */}
          <main className='px-6 py-6'>
            {sortedStudents.length === 0 ? (
              <p className='text-gray-500 text-[1.75rem]'>No students found in this classroom.</p>
            ) : (
              <div className='grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6'>
                {sortedStudents.map((student) => (
                  <StudentCard
                    key={student.sid}
                    student={student}
                    status={attendanceStatuses[student.userId] ?? 'present'}
                    onToggle={handleToggleStudent}
                  />
                ))}
              </div>
            )}
          </main>

          {/* Footer action bar */}
          <footer className='sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex gap-3 justify-end'>
            <button type='button' onClick={handleCancel} className={SECONDARY_BUTTON_SMALL_CLASS}>
              Cancel
            </button>
            <button type='button' onClick={handleSave} className={SECONDARY_BUTTON_SMALL_CLASS}>
              Save
            </button>
            <button
              type='button'
              onClick={handleSubmitClick}
              disabled={isSubmitDisabled}
              className={PRIMARY_BUTTON_SMALL_CLASS}
            >
              {isSubmitting ? 'Submitting…' : 'Submit Attendance'}
            </button>
          </footer>
        </>
      )}

      {/* Confirm submit overlay */}
      {showConfirmOverlay && (
        <div
          role='dialog'
          aria-modal='true'
          aria-labelledby='confirm-overlay-title'
          className='fixed inset-0 z-50 flex items-center justify-center bg-slate-900/35 px-4'
        >
          <div className='w-full max-w-[54rem] rounded-[1.8rem] bg-white px-7 py-6 shadow-[0_20px_60px_rgba(15,23,42,0.18)] sm:px-9 sm:py-7'>
            <div className='flex flex-col items-center text-center'>
              <CheckCircleIcon />
              <h2
                id='confirm-overlay-title'
                className='mt-3 text-[2.2rem] font-bold leading-tight text-[#0f1d4d] sm:text-[2.6rem]'
              >
                Confirm Attendance
              </h2>
              <p className='mt-3 text-[1.8rem] font-medium leading-relaxed text-[#23335d] sm:text-[2rem]'>
                <span className='font-bold text-[#00afd8]'>{attendanceCounts.present}</span> student
                {attendanceCounts.present !== 1 ? 's' : ''} present,{' '}
                <span className='font-bold text-[#f0a23b]'>{attendanceCounts.absent}</span> student
                {attendanceCounts.absent !== 1 ? 's' : ''} absent.
              </p>
            </div>

            <div className='mb-3 mt-4 h-px w-full bg-slate-200' />

            <div className='flex items-center justify-center gap-5 font-["GT-Walsheim-Pro-Bold"] text-[#00afd8]'>
              <Icon icon={CALENDAR_OUTLINE_ICON} className='h-12 w-12 shrink-0 text-[#00afd8]' aria-hidden='true' />
              <span className='text-[1.95rem] sm:text-[2.15rem]'>Date:</span>
              <span className='text-[1.95rem] sm:text-[2.15rem]'>
                {formatAttendanceDateForControl(attendanceDate, locale)}
              </span>
            </div>

            <div className='mt-7 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-5'>
              <button type='button' onClick={handleConfirmCancel} className={`${SECONDARY_BUTTON_SMALL_CLASS} w-full`}>
                Cancel
              </button>
              <button type='button' onClick={handleConfirmSubmit} className={`${PRIMARY_BUTTON_SMALL_CLASS} w-full`}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Result overlay — success or error */}
      {showResultOverlay && (
        <div
          role='dialog'
          aria-modal='true'
          aria-labelledby='result-overlay-title'
          className={[
            'fixed inset-0 z-50 flex items-center justify-center',
            submissionError ? 'bg-black/50' : 'bg-black/30',
          ].join(' ')}
        >
          <div className='bg-white rounded-xl shadow-xl p-8 max-w-sm w-full mx-4'>
            {submissionError ? (
              <>
                <h2 id='result-overlay-title' className='text-[2.5rem] font-bold text-red-700 mb-2'>
                  Submission Failed
                </h2>
                <p className='text-gray-600 text-[1.75rem] mb-6'>{submissionError}</p>
                <div className='flex justify-end'>
                  <button type='button' onClick={handleResultOkay} className={PRIMARY_BUTTON_CLASS}>
                    Okay
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 id='result-overlay-title' className='text-[2.5rem] font-bold text-green-700 mb-2'>
                  Attendance Submitted
                </h2>
                <p className='text-gray-500 text-[1.75rem]'>Redirecting you back to the unit…</p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TakweenAttendancePage;
